/**
 * An Insight agent's configuration (schema_version 1) and the editor's form for it.
 *
 * The limits mirror the service's pydantic model (poc/insight-agent agent_config.py), which
 * rejects unknown keys and oversized text. DOC itself stores any JSON, so this validation is what
 * stops a broken config from being saved; the service checks again on every run.
 */

export const SCHEMA_VERSION = 1;
export const ALL = '*';
export const BUILTIN_PREFIX = 'builtin:';
/** Skills published from the skills repo (cty-agent-skills), listed by the catalog with the built-ins. */
export const SKILL_PREFIX = 'skill:';

export const LIMITS = {
  role: 200,
  goal: 2_000,
  section: 8_000,
  guidance: 20,
  alwaysGuidance: 16_000,
  knowledgeSources: 10,
  label: 200,
  whenToUse: 500,
  guidanceDescription: 500,
  guidanceBody: 16_000,
  historyTurns: [0, 5],
  maxIter: [4, 20],
  maxSqlRetries: [1, 5],
} as const;

const SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const DATASET = /^[a-z0-9_]+\.[a-z0-9_]+$/;

export type GuidanceMode = 'always' | 'on_demand';

export interface GuidanceRef {
  id: string;
  version_id?: string | null;
  mode: GuidanceMode;
}

export interface KnowledgeSource {
  key: string;
  advisor_id: string;
  label?: string;
  when_to_use: string;
}

export interface AgentConfig {
  schema_version: 1;
  model: { name: string; temperature: number };
  prompt: { role: string; goal: string; backstory: string; instructions: string; answer_format: string };
  guidance: GuidanceRef[];
  knowledge_sources: KnowledgeSource[];
  data_sources: { datasets: string[]; metrics: string[] };
  conversation: { history_turns: number };
  limits: { max_iter: number; max_sql_retries: number };
}

/** The `config` of a guidance item: a built-in from the catalog or a tenant row. */
export interface GuidanceItem {
  name: string;
  description: string;
  body: string;
}

/**
 * A catalog guidance item. A published skill (`skill:<name>`) may also say which version its channel
 * serves (`version`) and which versions can be pinned (`versions`); older runtimes send neither.
 */
export type CatalogGuidanceItem = GuidanceItem & {
  id: string;
  version?: string | number | null;
  versions?: unknown;
};

export interface InsightCatalog {
  models: string[];
  fixed_rules: string;
  builtin_guidance: CatalogGuidanceItem[];
  default_config: AgentConfig;
  /** DOC code of the tenant's own Insight Agent, the default that answers when no agent is chosen. */
  default_agent_code?: string;
}

/** What the editor edits. Numbers stay text until validation so a half-typed value is not lost. */
export interface AgentForm {
  name: string;
  description: string;
  model: string;
  temperature: string;
  role: string;
  goal: string;
  backstory: string;
  instructions: string;
  answerFormat: string;
  guidance: GuidanceRef[];
  knowledge: KnowledgeRow[];
  allDatasets: boolean;
  datasets: string[];
  allMetrics: boolean;
  metrics: string[];
  historyTurns: string;
  maxIter: string;
  maxSqlRetries: string;
}

/** A knowledge source as edited; `uid` only keys the row on screen and is never saved. */
export interface KnowledgeRow {
  uid: string;
  key: string;
  advisor_id: string;
  label: string;
  when_to_use: string;
}

let rowCount = 0;
const rowUid = () => {
  rowCount += 1;
  return `knowledge-${rowCount}`;
};

export function newKnowledgeRow(source: Partial<KnowledgeSource> = {}): KnowledgeRow {
  return {
    uid: rowUid(),
    key: source.key ?? '',
    advisor_id: source.advisor_id ?? '',
    label: source.label ?? '',
    when_to_use: source.when_to_use ?? '',
  };
}

export const isBuiltin = (id: string) => id.startsWith(BUILTIN_PREFIX);
export const isPublishedSkill = (id: string) => id.startsWith(SKILL_PREFIX);

/** A version of a published skill that an agent can pin: `id` goes in the guidance ref's version_id. */
export interface SkillVersion {
  id: string;
  label: string;
}

const versionText = (value: unknown) =>
  (typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '');

/** The version a published skill's channel serves now, or '' when the catalog does not say. */
export const channelVersion = (item: Pick<CatalogGuidanceItem, 'version'>) => versionText(item.version);

/**
 * A published skill's pinnable versions, newest first. The catalog lists them oldest first, as
 * version strings (`"versions": ["1.0.0", "1.1.0"]`); objects naming one (`version_id`, `id` or
 * `version`) are read too. Anything else is skipped, so a catalog without the field (an older
 * runtime), or with a shape this UI does not know, pins nothing.
 */
export function skillVersions(raw: unknown): SkillVersion[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const versions: SkillVersion[] = [];
  for (const entry of raw) {
    const fields = entry && typeof entry === 'object' ? entry as Record<string, unknown> : null;
    const id = fields ? versionText(fields.version_id) || versionText(fields.id) || versionText(fields.version) : versionText(entry);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    versions.push({ id, label: (fields && (versionText(fields.label) || versionText(fields.version))) || id });
  }
  return versions.reverse();
}

const text = (value: unknown) => (typeof value === 'string' ? value : '');
const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
const num = (value: unknown, fallback: number) => String(typeof value === 'number' ? value : fallback);

/** A stored (or default) config and its aiworker's name and description, as the form edits them. */
export function configToForm(config: Partial<AgentConfig> | null | undefined, worker?: { name?: string; description?: string }): AgentForm {
  const c = (config ?? {}) as Partial<AgentConfig>;
  const datasets = list<string>(c.data_sources?.datasets ?? [ALL]);
  const metrics = list<string>(c.data_sources?.metrics ?? [ALL]);
  return {
    name: worker?.name ?? '',
    description: worker?.description ?? '',
    model: text(c.model?.name),
    temperature: num(c.model?.temperature, 0.1),
    role: text(c.prompt?.role),
    goal: text(c.prompt?.goal),
    backstory: text(c.prompt?.backstory),
    instructions: text(c.prompt?.instructions),
    answerFormat: text(c.prompt?.answer_format),
    guidance: list<GuidanceRef>(c.guidance).map((g) => ({
      id: text(g.id),
      mode: g.mode === 'on_demand' ? 'on_demand' : 'always',
      ...(g.version_id ? { version_id: g.version_id } : {}),
    })),
    knowledge: list<KnowledgeSource>(c.knowledge_sources).map((k) => newKnowledgeRow({
      key: text(k.key),
      advisor_id: text(k.advisor_id),
      label: text(k.label),
      when_to_use: text(k.when_to_use),
    })),
    allDatasets: datasets.includes(ALL),
    datasets: datasets.filter((d) => d !== ALL),
    allMetrics: metrics.includes(ALL),
    metrics: metrics.filter((m) => m !== ALL),
    historyTurns: num(c.conversation?.history_turns, 3),
    maxIter: num(c.limits?.max_iter, 12),
    maxSqlRetries: num(c.limits?.max_sql_retries, 3),
  };
}

/**
 * The config to store or preview. It names only schema keys, so it can never carry the keys DOC
 * strips from responses (tenant_id, workflow_data, bot_data). Built-ins never get a version_id; a
 * published skill keeps the one it is pinned to, and follows its channel without one.
 */
export function formToConfig(form: AgentForm): AgentConfig {
  return {
    schema_version: SCHEMA_VERSION,
    model: { name: form.model.trim(), temperature: Number(form.temperature) },
    prompt: {
      role: form.role,
      goal: form.goal,
      backstory: form.backstory,
      instructions: form.instructions,
      answer_format: form.answerFormat,
    },
    guidance: form.guidance.map((g) => ({
      id: g.id,
      mode: g.mode,
      ...(g.version_id && !isBuiltin(g.id) ? { version_id: g.version_id } : {}),
    })),
    knowledge_sources: form.knowledge.map((k) => ({
      key: k.key.trim(),
      advisor_id: k.advisor_id,
      ...(k.label.trim() ? { label: k.label.trim() } : {}),
      when_to_use: k.when_to_use.trim(),
    })),
    data_sources: {
      datasets: form.allDatasets ? [ALL] : form.datasets.map((d) => d.toLowerCase()),
      metrics: form.allMetrics ? [ALL] : form.metrics,
    },
    conversation: { history_turns: Number(form.historyTurns) },
    limits: { max_iter: Number(form.maxIter), max_sql_retries: Number(form.maxSqlRetries) },
  };
}

export interface ValidationContext {
  models: string[];
  /** Name and body of every guidance item the form may attach, by id (active versions). */
  guidance: Record<string, GuidanceItem>;
}

/** Field path (as in the service's 422 messages) to a message. Empty when the form can be saved. */
export type FormErrors = Record<string, string>;

function wholeNumber(value: string, [min, max]: readonly [number, number]): boolean {
  const n = Number(value);
  return value.trim() !== '' && Number.isInteger(n) && n >= min && n <= max;
}

export function validateForm(form: AgentForm, context: ValidationContext): FormErrors {
  const errors: FormErrors = {};
  const need = (key: string, value: string, max: number, required = true) => {
    if (required && !value.trim()) errors[key] = 'Required.';
    else if (value.length > max) errors[key] = `At most ${max.toLocaleString()} characters (now ${value.length.toLocaleString()}).`;
  };

  need('name', form.name, 200);
  need('description', form.description, 2_000);
  if (!form.model) errors['model.name'] = 'Required.';
  else if (!context.models.includes(form.model)) errors['model.name'] = `${form.model} is not an allowed model.`;
  const temperature = Number(form.temperature);
  if (form.temperature.trim() === '' || Number.isNaN(temperature) || temperature < 0 || temperature > 1) {
    errors['model.temperature'] = 'A number from 0 to 1.';
  }

  need('prompt.role', form.role, LIMITS.role);
  need('prompt.goal', form.goal, LIMITS.goal);
  need('prompt.backstory', form.backstory, LIMITS.section, false);
  need('prompt.instructions', form.instructions, LIMITS.section);
  need('prompt.answer_format', form.answerFormat, LIMITS.section, false);

  if (form.guidance.length > LIMITS.guidance) errors.guidance = `At most ${LIMITS.guidance} guidance items.`;
  const ids = new Set<string>();
  const names = new Set<string>();
  let alwaysChars = 0;
  form.guidance.forEach((ref, index) => {
    const item = context.guidance[ref.id];
    if (ids.has(ref.id)) errors[`guidance.${index}`] = 'This item is attached twice.';
    else if (!item) errors[`guidance.${index}`] = 'This guidance item was not found.';
    else if (names.has(item.name)) errors[`guidance.${index}`] = `Two attached items are named ${item.name}.`;
    ids.add(ref.id);
    if (item) {
      names.add(item.name);
      // A pinned version is counted at its active size; the service re-checks with the pinned body.
      if (ref.mode === 'always') alwaysChars += item.body.length;
    }
  });
  if (!errors.guidance && alwaysChars > LIMITS.alwaysGuidance) {
    errors.guidance = `Guidance attached "always" totals ${alwaysChars.toLocaleString()} characters; the limit is ${LIMITS.alwaysGuidance.toLocaleString()}. Attach some on demand.`;
  }

  if (form.knowledge.length > LIMITS.knowledgeSources) {
    errors.knowledge_sources = `At most ${LIMITS.knowledgeSources} knowledge sources.`;
  }
  const keys = new Set<string>();
  form.knowledge.forEach((source, index) => {
    const at = `knowledge_sources.${index}`;
    const key = source.key.trim();
    if (!SLUG.test(key)) errors[`${at}.key`] = 'Lowercase letters, digits, _ and -, starting with a letter or digit (at most 64).';
    else if (keys.has(key)) errors[`${at}.key`] = 'Keys must be unique.';
    keys.add(key);
    if (!source.advisor_id) errors[`${at}.advisor_id`] = 'Pick an advisor.';
    if (source.label.length > LIMITS.label) errors[`${at}.label`] = `At most ${LIMITS.label} characters.`;
    need(`${at}.when_to_use`, source.when_to_use, LIMITS.whenToUse);
  });

  if (!form.allDatasets) {
    if (form.datasets.length === 0) errors['data_sources.datasets'] = 'Pick at least one dataset, or All.';
    else if (form.datasets.some((d) => !DATASET.test(d.toLowerCase()))) {
      errors['data_sources.datasets'] = 'Dataset names look like agent_history.agent_runs.';
    }
  }
  if (!form.allMetrics && form.metrics.some((m) => !SLUG.test(m))) {
    errors['data_sources.metrics'] = 'Metric keys look like automation_rate.';
  }

  if (!wholeNumber(form.historyTurns, LIMITS.historyTurns)) errors['conversation.history_turns'] = 'A whole number from 0 to 5.';
  if (!wholeNumber(form.maxIter, LIMITS.maxIter)) errors['limits.max_iter'] = 'A whole number from 4 to 20.';
  if (!wholeNumber(form.maxSqlRetries, LIMITS.maxSqlRetries)) errors['limits.max_sql_retries'] = 'A whole number from 1 to 5.';
  return errors;
}

export function validateGuidanceItem(item: GuidanceItem, takenNames: string[]): FormErrors {
  const errors: FormErrors = {};
  if (!SLUG.test(item.name)) errors.name = 'Lowercase letters, digits, _ and -, starting with a letter or digit (at most 64).';
  else if (takenNames.includes(item.name)) errors.name = 'Another guidance item already has this name.';
  if (!item.description.trim()) errors.description = 'Required.';
  else if (item.description.length > LIMITS.guidanceDescription) errors.description = `At most ${LIMITS.guidanceDescription} characters.`;
  if (!item.body.trim()) errors.body = 'Required.';
  else if (item.body.length > LIMITS.guidanceBody) errors.body = `At most ${LIMITS.guidanceBody.toLocaleString()} characters.`;
  return errors;
}

/** A knowledge-source key from an advisor's name: "Warranty Policy" -> "warranty_policy". */
export function slugify(name: string): string {
  return name.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).join('_').slice(0, 64) || 'source';
}

/** `automation_rate(start_date, end_date)` as /insight-agent/access lists it -> `automation_rate`. */
export function metricKey(signature: string): string {
  return signature.split('(')[0].trim();
}

/** `{a: {b: 1}, c: [{d: 2}]}` -> `{'a.b': '1', 'c.0.d': '2'}`, so the compare modal can diff it row by row. */
export function flattenConfig(value: unknown, prefix = ''): Record<string, string> {
  if (value !== null && typeof value === 'object') {
    const entries = Array.isArray(value) ? value.map((v, i) => [String(i), v] as const) : Object.entries(value);
    if (entries.length === 0) return prefix ? { [prefix]: JSON.stringify(value) } : {};
    return Object.assign({}, ...entries.map(([key, v]) => flattenConfig(v, prefix ? `${prefix}.${key}` : key)));
  }
  return prefix ? { [prefix]: typeof value === 'string' ? value : JSON.stringify(value) } : {};
}
